import { makeAutoObservable, runInAction } from "mobx";
import { THROW_ON_ERROR } from "shared/dialogSubmit";
import type RootStore from "stores/RootStore";
import { isOperator } from "auth/tier";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  normalizeWorkItem,
  buildWorkItemPatchOps,
  DEFAULT_WIQL,
  type DevOpsConnection,
  type DevOpsProject,
  type DevOpsWorkItem,
  type DevOpsWorkItemApiRecord,
  type DevOpsWorkItemFormData,
  type DevOpsWorkItemUpdatePayload,
  type DevOpsEvent,
  type DevOpsOverviewClientRef,
} from "../devops.types";
import type DevOpsScopeStore from "../shared/DevOpsScopeStore";
import { swallowedFailure } from "../shared/swallowedFind";
import { clientsForProject, type ProjectClientsView } from "../shared/projectClients";
import { subscribeDevOpsEvents } from "../devopsLiveStatus";
import { exportToCsv } from "shared/common/utils/export";
import {
  WORK_ITEM_EXPORT_COLUMNS,
  slugForFileName,
  toWorkItemExportRow,
  type DevOpsExportScope,
  type ExportRecord,
} from "../devopsExports";
import {
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

export type WorkItemSortKey =
  | "id"
  | "workItemType"
  | "title"
  | "state"
  | "assignedTo";

/** Sort keys that read best biggest-first on first click (numeric id ≈ newest). */
const DESC_FIRST: ReadonlySet<WorkItemSortKey> = new Set(["id"]);

/**
 * Normalise a ServiceStore `find()` response into a plain array of rows.
 * Paginated services return `{ data, total }`; custom non-paginated finds may
 * return the bare array — tolerate both. (Replaces the removed `unwrapList`.)
 */
function rowsOf<T>(result: { data?: T[] } | T[] | null | undefined): T[] {
  if (Array.isArray(result)) return result;
  return result?.data ?? [];
}

/**
 * View store for the Azure DevOps Work Items view.
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do NOT
 * extend ViewStore. Backed by registered `@intrasync/stores` ServiceStores
 * (`devops-connections`, `devops-projects`, `devops-work-items`) which hold the
 * encrypted PAT server-side — no secret is ever handled client-side.
 *
 * Reads run a WIQL query (`find` with `{ connectionId, project, wiql }`);
 * writes patch a work item's state/fields (`patch` with a JSON-Patch envelope).
 * A live `devops-event` subscription patches changed work-item rows in place.
 *
 * `connections` is a computed view over the `devops-connections` entity cache.
 * `projects` and the WIQL `workItems` are connection/project-scoped and
 * re-queried per selection, so they are read from each `find()` RESPONSE (the
 * entity cache accumulates across queries and would leak rows across scopes).
 *
 * WHICH connection/project is the shared `devopsScopeStore`'s fact, not this
 * store's — see PipelinesBoardStore for the rule.
 */
class WorkItemsStore {
  rootStore: RootStore;

  /** The scope's connection's live project list (the header's picker). */
  projects: DevOpsProject[] = [];

  connectionsLoading = false;
  projectsLoading = false;

  // WIQL query + results.
  wiql = DEFAULT_WIQL;
  workItems: DevOpsWorkItem[] = [];
  loading = false;
  error: string | null = null;

  // Header filters (applied client-side over the WIQL result set).
  searchQuery = "";
  /** "" = all states; otherwise an exact System.State value. */
  stateFilter = "";
  /** "" = all types; otherwise an exact System.WorkItemType value. */
  typeFilter = "";

  // Column sort (applied client-side over the filtered rows, like the filters)
  sortKey: WorkItemSortKey = "id";
  sortDir: SortDir = "desc";

  // Update form (work-item state/fields).
  editingWorkItemId: string | null = null;
  formData: DevOpsWorkItemFormData = {};
  saving = false;
  formError: string | null = null;

  /**
   * Sequence stamps for the two SCOPED reads — the same discipline as
   * `DevOpsSubscriptionsStore.projectsSeq` and `DevOpsProjectStore.loadSeq`.
   * Project A is chosen, then B. B answers first and paints; A's slower
   * answer must NOT repaint the grid while the shared scope says B, or the
   * page would offer B's project beside A's rows and an action would combine
   * the two. A superseded read writes nothing at all — not the rows, not its
   * error, and not the spinner its `finally` would otherwise clear out from
   * under the read that is still running.
   */
  private projectsSeq = 0;
  private workItemsSeq = 0;

  private unsubscribeLive: (() => void) | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ---- ServiceStore accessors ----

  private get connectionsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("devops-connections") ?? this.rootStore.devopsConnectionsStore) as unknown as ServiceStoreType | null) ?? null;
  }

  private get projectsService(): ServiceStoreType | null {
    return (this.rootStore.devopsProjectsStore as ServiceStoreType | null) ?? null;
  }

  private get workItemsService(): ServiceStoreType | null {
    return (this.rootStore.devopsWorkItemsStore as ServiceStoreType | null) ?? null;
  }

  /** Connection list — computed over the `devops-connections` entity cache. */
  get connections(): DevOpsConnection[] {
    return (this.connectionsService?.entities ?? []) as unknown as DevOpsConnection[];
  }

  // ---- The shared scope ----------------------------------------------------

  private get scopeStore(): DevOpsScopeStore | null {
    return (this.rootStore.devopsScopeStore as DevOpsScopeStore | undefined) ?? null;
  }

  get selectedConnectionId(): string | null {
    return this.scopeStore?.connectionId ?? null;
  }

  get selectedProjectId(): string | null {
    return this.scopeStore?.projectId ?? null;
  }

  /**
   * Clients linked to the scoped PROJECT (from a loaded overview; never
   * fetched here). Distinct from {@link selectedClientId}, which is the
   * CONNECTION's client — both are shown, because they answer different
   * questions.
   */
  get projectClients(): ProjectClientsView {
    return clientsForProject(
      this.rootStore,
      this.selectedConnectionId,
      this.selectedProjectId
    );
  }

  get clientsForCurrentProject(): DevOpsOverviewClientRef[] {
    return this.projectClients.clients;
  }

  // ---- Derived -------------------------------------------------------------

  /**
   * Whether the current user may update work items (operator/admin tier). The
   * API is the real enforcement point (devops RBAC `requireActionAccess`); this
   * only governs whether the Update action renders. Tier resolution is
   * centralised in `auth/tier.ts` — widen there, never at the call sites.
   */
  get canManage(): boolean {
    return isOperator(this.rootStore);
  }

  get selectedConnection(): DevOpsConnection | null {
    if (!this.selectedConnectionId) return null;
    return (
      this.connections.find((c) => c.id === this.selectedConnectionId) || null
    );
  }

  /** The global ClientStore (the full tenant list) — name resolution only. */
  private get globalClientStore():
    | { clients?: Array<{ id: string; name?: string }> }
    | undefined {
    return (this.rootStore as Record<string, unknown>).clientStore as
      | { clients?: Array<{ id: string; name?: string }> }
      | undefined;
  }

  /**
   * Client attribution for the open scope. Azure work items carry no IntraSync
   * client of their own, but a connection may be bound to one
   * (`devops_connections.clientId`; null = the internal org), so the whole
   * result set belongs to that client. Connection-level is deliberately as far
   * as this goes — there is no per-item join.
   */
  get selectedClientId(): string | null {
    return this.selectedConnection?.clientId ?? null;
  }

  /** Display name for {@link selectedClientId} (falls back to the raw id). */
  get selectedClientName(): string {
    const clientId = this.selectedClientId;
    if (!clientId) return "";
    const match = this.globalClientStore?.clients?.find(
      (c) => c.id === clientId
    );
    return match?.name || clientId;
  }

  get editingWorkItem(): DevOpsWorkItem | null {
    if (!this.editingWorkItemId) return null;
    return this.workItems.find((w) => w.id === this.editingWorkItemId) || null;
  }

  get isEditing(): boolean {
    return !!this.editingWorkItemId;
  }

  get totalCount(): number {
    return this.workItems.length;
  }

  /** How many items survive the header filters — what the table renders. */
  get filteredCount(): number {
    return this.filteredWorkItems.length;
  }

  /**
   * The sentence the board prints under the table, or null when there is
   * nothing loaded to qualify.
   *
   * This board does NOT show the whole backlog: it shows whatever the WIQL
   * query returned, and Azure DevOps pages WIQL results server-side. Nothing in
   * the proxy's response distinguishes "that was everything" from "that was the
   * first page", so the only honest thing to print is the count we have plus
   * the fact that it may not be all of them. Users default to assuming a list
   * is complete; saying so explicitly is the whole point.
   */
  get upstreamPagingNote(): string | null {
    if (this.totalCount === 0) return null;
    return `Showing the ${this.totalCount} work ${
      this.totalCount === 1 ? "item" : "items"
    } this query returned. Azure DevOps pages WIQL results server-side, so a broad query may not list every match — narrow the WIQL to be sure.`;
  }

  /**
   * The connection/project a row was read through, resolved to display names.
   * Stamped on every exported row: a board CSV with no scope column is
   * uninterpretable a week later.
   */
  get exportScope(): DevOpsExportScope {
    return {
      connectionName: this.selectedConnection?.name ?? "",
      projectName: this.selectedProject?.name ?? this.selectedProjectId ?? "",
      clientName: this.selectedClientName,
    };
  }

  /** The selected project record (name resolution for the export scope). */
  get selectedProject(): DevOpsProject | null {
    if (!this.selectedProjectId) return null;
    return this.projects.find((p) => p.id === this.selectedProjectId) || null;
  }

  // ---- CSV export ---------------------------------------------------------

  /**
   * The work-item list as flat CSV records — exactly what is ON SCREEN
   * (`sortedWorkItems` = state/type/search filters, then the active sort).
   */
  get exportRows(): ExportRecord[] {
    const scope = this.exportScope;
    return this.sortedWorkItems.map((item) => toWorkItemExportRow(item, scope));
  }

  get canExport(): boolean {
    return this.exportRows.length > 0;
  }

  /** Download the on-screen work items as CSV. False (never a throw) on an
   *  empty set; the header also disables the button on {@link canExport}. */
  exportCsv(): boolean {
    const rows = this.exportRows;
    if (rows.length === 0) return false;
    try {
      const project = this.exportScope.projectName || "work-items";
      exportToCsv(rows, WORK_ITEM_EXPORT_COLUMNS, {
        fileName: `work-items-${slugForFileName(project)}-${new Date()
          .toISOString()
          .slice(0, 10)}`,
      });
      return true;
    } catch (err) {
      console.error("Work items CSV export failed:", err);
      return false;
    }
  }

  get openCount(): number {
    return this.workItems.filter(
      (w) => w.state && w.state !== "Closed" && w.state !== "Removed" && w.state !== "Done"
    ).length;
  }

  get closedCount(): number {
    return this.workItems.filter(
      (w) => w.state === "Closed" || w.state === "Done"
    ).length;
  }

  get filteredWorkItems(): DevOpsWorkItem[] {
    let result = this.workItems;

    if (this.stateFilter) {
      result = result.filter((w) => w.state === this.stateFilter);
    }
    if (this.typeFilter) {
      result = result.filter((w) => w.workItemType === this.typeFilter);
    }
    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      result = result.filter(
        (w) =>
          w.title?.toLowerCase().includes(q) ||
          w.assignedTo?.toLowerCase().includes(q)
      );
    }

    return result;
  }

  /**
   * The rows the table renders: filters, then the active column sort. Nulls
   * (e.g. unassigned items) sort last in either direction.
   */
  get sortedWorkItems(): DevOpsWorkItem[] {
    const key = this.sortKey;
    const valueOf = (w: DevOpsWorkItem): SortValue => {
      switch (key) {
        case "id": {
          const n = Number(w.id);
          return Number.isFinite(n) ? n : textValue(w.id);
        }
        case "workItemType":
          return textValue(w.workItemType);
        case "title":
          return textValue(w.title);
        case "state":
          return textValue(w.state);
        case "assignedTo":
          return textValue(w.assignedTo);
      }
    };
    return sortRows(this.filteredWorkItems, valueOf, this.sortDir);
  }

  /** Distinct states present in the loaded result set — the State filter's options. */
  get stateOptions(): string[] {
    const states = new Set<string>();
    for (const w of this.workItems) if (w.state) states.add(w.state);
    return [...states].sort((a, b) => a.localeCompare(b));
  }

  /** Distinct work-item types in the loaded result set — the Type filter's options. */
  get typeOptions(): string[] {
    const types = new Set<string>();
    for (const w of this.workItems) if (w.workItemType) types.add(w.workItemType);
    return [...types].sort((a, b) => a.localeCompare(b));
  }

  // ---- Filter / sort setters -----------------------------------------------

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  setStateFilter(state: string) {
    this.stateFilter = state;
  }

  setTypeFilter(type: string) {
    this.typeFilter = type;
  }

  /** Column-header click: same column toggles direction, a new column starts
   *  in its natural direction (id newest-first, text A→Z). */
  setSort(key: WorkItemSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  /** The filter options derive from the loaded rows, so clear the filters when
   *  the scope changes — a stale state/type would silently empty the new list. */
  private resetFilters() {
    this.searchQuery = "";
    this.stateFilter = "";
    this.typeFilter = "";
  }

  // ---- Lifecycle / loading -------------------------------------------------

  /**
   * Entry point from the route (and from a Project 360 tab). A scope chosen
   * anywhere else is honoured as-is; only an EMPTY scope auto-picks.
   */
  async initialize() {
    this.startLiveStatus();
    const ended = sessionMark(this.rootStore);
    await this.loadConnections();
    // A sign-out during the read leaves `error` null (it is no failure of the
    // next person's), so the `error` check below cannot end the sequence.
    if (ended()) return;
    // A remembered connection that no longer exists is dropped (with its
    // project) so the auto-pick below runs — never after a FAILED load, which
    // `loadConnections` records on `error`. See PipelinesBoardStore.initialize.
    // A failed connections load ends the sequence. There is no list to
    // reconcile against and no connection to read a project for, and going on
    // would replace the CAUSAL error with whatever the doomed project read
    // reports — telling the operator the projects are unavailable when the
    // truth is that HQ could not reach the connections at all.
    if (this.error) return;
    this.scopeStore?.reconcileConnections(this.connections.map((c) => c.id));
    if (this.selectedConnectionId) {
      await this.adoptScope();
    } else if (this.connections.length > 0) {
      await this.selectConnection(this.connections[0].id);
    }
  }

  /**
   * Load this board for a scope set elsewhere. Same rule as
   * PipelinesBoardStore.adoptScope: a project the connection no longer lists
   * is cleared (project only), nothing is guessed in its place, and a failed
   * project read leaves the scope untouched.
   */
  private async adoptScope() {
    const ended = sessionMark(this.rootStore);
    await this.loadProjects();
    // As in initialize: a sign-out leaves no error, and going on would clear
    // the shared scope's project against the list reset() emptied.
    if (ended() || this.error) return;
    const projectId = this.selectedProjectId;
    if (!projectId) {
      runInAction(() => {
        this.workItems = [];
        this.resetFilters();
      });
      return;
    }
    const project = this.projects.find((p) => p.id === projectId);
    if (!project) {
      runInAction(() => {
        this.scopeStore?.setProject(null);
        this.workItems = [];
        this.resetFilters();
      });
      return;
    }
    if (!this.scopeStore?.projectName) {
      this.scopeStore?.setScope({ projectName: project.name });
    }
    await this.runQuery();
  }

  async loadConnections() {
    this.connectionsLoading = true;
    this.error = null;
    try {
      // ServiceStore.find populates its entity cache, which `connections` reads.
      const result = await this.connectionsService?.find({
        query: { $limit: 200, $sort: { name: 1 } },
      });
      // A null result is a SWALLOWED rejection (see shared/swallowedFind.ts),
      // never an org with no connections. It matters twice over here: the
      // cache is empty on a fresh session (`enableStorage: false`), so a
      // silent failure would hand `initialize`'s reconcile an empty list and
      // wipe the remembered scope — the one thing reconcileConnections is
      // documented never to do on a failed load.
      if (result == null) {
        throw swallowedFailure(this.connectionsService, "errorOnFind", "Failed to load connections");
      }
    } catch (err: unknown) {
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : "Failed to load connections";
      });

      console.error("Failed to load DevOps connections:", err);
    } finally {
      runInAction(() => {
        this.connectionsLoading = false;
      });
    }
  }

  /** Header selector — writes the shared scope (a new connection clears the project). */
  async selectConnection(connectionId: string | null) {
    runInAction(() => {
      // Invalidate both reads in flight for the scope being left. The loaders
      // below stamp their own sequence when a selection IS made, but clearing
      // one loads nothing — without this bump the previous scope's answer
      // still matches and paints itself onto a board that has moved on.
      this.projectsSeq += 1;
      this.workItemsSeq += 1;
      this.projectsLoading = false;
      this.loading = false;
      this.scopeStore?.setScope({ connectionId, projectId: null, projectName: null });
      this.projects = [];
      this.workItems = [];
      this.resetFilters();
    });
    if (connectionId) {
      await this.loadProjects();
      const conn = this.selectedConnection;
      const defaultProj =
        (conn?.defaultProject &&
          this.projects.find(
            (p) => p.name === conn.defaultProject || p.id === conn.defaultProject
          )) ||
        this.projects[0];
      if (defaultProj) {
        await this.selectProject(defaultProj.id);
      }
    }
  }

  async loadProjects() {
    if (!this.selectedConnectionId) return;
    const seq = ++this.projectsSeq;
    this.projectsLoading = true;
    try {
      // Scoped Azure proxy → read the find() response rows.
      // No `$limit`: `devops-projects.find` reads only `connectionId` and
      // calls Azure's core.getProjects(), returning whatever page Azure gives
      // back. A `$limit` here was inert — it looked like an enforced cap and
      // was not one. Azure's own paging is the real bound; if an org ever has
      // more projects than one page, the picker needs a search, not a number.
      const result = await this.projectsService?.find({
        query: { connectionId: this.selectedConnectionId },
      });
      // A null result is a swallowed failure, not an empty org (see
      // shared/swallowedFind.ts) — adoptScope must not clear the project on it.
      if (seq !== this.projectsSeq) return; // a newer scope superseded this
      if (result == null) {
        throw swallowedFailure(this.projectsService, "errorOnFind", "Failed to load projects");
      }
      runInAction(() => {
        this.projects = rowsOf<DevOpsProject>(
          result as unknown as { data?: DevOpsProject[] } | DevOpsProject[],
        );
      });
    } catch (err: unknown) {
      // A superseded read reports nothing: its failure belongs to a scope
      // this board has left.
      if (seq !== this.projectsSeq) return;
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to load projects";
        }
      });

      console.error("Failed to load DevOps projects:", err);
    } finally {
      runInAction(() => {
        // Only the newest read owns the spinner.
        if (seq === this.projectsSeq) this.projectsLoading = false;
      });
    }
  }

  /** Header selector — writes the shared scope, name included. */
  async selectProject(projectId: string | null) {
    runInAction(() => {
      // Invalidate the WIQL read in flight for the project being left.
      this.workItemsSeq += 1;
      this.loading = false;
      this.scopeStore?.setProject(
        projectId,
        this.projects.find((p) => p.id === projectId)?.name ?? null
      );
      this.workItems = [];
      this.resetFilters();
    });
    if (projectId) {
      await this.runQuery();
    }
  }

  setWiql(wiql: string) {
    this.wiql = wiql;
  }

  /**
   * Run the current WIQL query against the selected connection/project. The API
   * (`devops-work-items` find) executes the WIQL through the connection's
   * encrypted PAT and returns hydrated work items; we flatten the Azure `fields`
   * dictionary into the table shape.
   */
  async runQuery() {
    if (!this.selectedConnectionId || !this.selectedProjectId) return;
    const seq = ++this.workItemsSeq;
    this.loading = true;
    this.error = null;
    try {
      // Scoped WIQL proxy → read the find() response rows, not the cache.
      //
      // NO `$limit`, and removing it did not raise a cap — there never was one
      // here. `devops-work-items.find` reads connectionId/project/wiql off the
      // query and IGNORES everything else: it runs the WIQL through
      // queryByWiql, then hydrates every returned id in chunks of 200. The
      // `$limit: 200` this used to send was inert, and an inert limit is worse
      // than none — it reads as an enforced cap, which invites "just raise it"
      // as the fix for a short list when the real cap is upstream.
      //
      // The REAL cap is Azure's: queryByWiql returns at most the first page of
      // matching ids for a broad query, and never more than Azure's own WIQL
      // ceiling. Nothing in the response says whether that happened, so the
      // board DISCLOSES the possibility rather than pretending to detect it —
      // see {@link upstreamPagingNote}. The way to shrink a result set here is
      // the WIQL itself, which the user can edit.
      const result = await this.workItemsService?.find({
        query: {
          connectionId: this.selectedConnectionId,
          // The API reads the scoping project from `query.project` (NOT
          // projectId) — it throws BadRequest('project is required') otherwise.
          project: this.selectedProjectId,
          wiql: this.wiql,
        },
      });
      if (seq !== this.workItemsSeq) return; // a newer project superseded this
      // A null result is a SWALLOWED rejection (see shared/swallowedFind.ts),
      // never a WIQL query that matched nothing — and must not empty a
      // populated grid with nothing saying the read failed.
      if (result == null) {
        throw swallowedFailure(this.workItemsService, "errorOnFind", "Failed to run query");
      }
      runInAction(() => {
        this.workItems = rowsOf<DevOpsWorkItemApiRecord>(
          result as unknown as
            | { data?: DevOpsWorkItemApiRecord[] }
            | DevOpsWorkItemApiRecord[],
        ).map(normalizeWorkItem);
      });
    } catch (err: unknown) {
      // A superseded read reports nothing: its failure belongs to a project
      // this board has left.
      if (seq !== this.workItemsSeq) return;
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.error =
            err instanceof Error ? err.message : "Failed to run query";
        }
      });

      console.error("WIQL query failed:", err);
    } finally {
      runInAction(() => {
        // Only the newest read owns the spinner.
        if (seq === this.workItemsSeq) this.loading = false;
      });
    }
  }

  /** Header refresh / post-write callback. */
  async reload() {
    if (this.selectedConnectionId && this.selectedProjectId) {
      await this.runQuery();
    } else {
      await this.initialize();
    }
  }

  // ---- Update form ---------------------------------------------------------

  startEdit(workItem: DevOpsWorkItem) {
    this.editingWorkItemId = workItem.id;
    this.formError = null;
    this.formData = {
      state: workItem.state || "",
      title: workItem.title || "",
      assignedTo: workItem.assignedTo ?? "",
      priority: workItem.priority ?? null,
      tags: workItem.tags ?? "",
    };
  }

  setFormField(
    field: keyof DevOpsWorkItemFormData,
    value: string | number | null
  ) {
    this.formData = { ...this.formData, [field]: value } as DevOpsWorkItemFormData;
  }

  get isFormValid(): boolean {
    // State is the primary editable field; require it (others optional).
    return !!this.formData.state?.trim();
  }

  /**
   * Persist the work-item update. The API maps the named props back to Azure
   * JSON-patch field reference names and applies them through the connection's
   * PAT. connectionId/projectId scope the write. No secret is sent.
   */
  async saveWorkItem(): Promise<boolean> {
    if (!this.editingWorkItemId) return false;
    if (!this.selectedConnectionId || !this.selectedProjectId) return false;
    if (!this.isFormValid) {
      this.formError = "State is required.";
      return false;
    }
    this.saving = true;
    this.formError = null;
    const id = this.editingWorkItemId;
    try {
      // The API patch requires a raw Azure JSON-Patch document under `ops` plus
      // `connectionId` + `project` (NOT named fields / `projectId`). Assemble the
      // ops from the form; only fields the user set are emitted.
      const ops = buildWorkItemPatchOps(this.formData);
      if (ops.length === 0) {
        runInAction(() => {
          this.formError = "Nothing to update.";
          this.saving = false;
        });
        return false;
      }
      const payload: DevOpsWorkItemUpdatePayload = {
        connectionId: this.selectedConnectionId,
        project: this.selectedProjectId,
        ops,
      };

      // The patch body is the JSON-Patch envelope, not a partial DevOpsWorkItem;
      // pass it as an opaque payload to the ServiceStore patch.
      // THROW_ON_ERROR: a refusal otherwise RESOLVES null and read as saved.
      await this.workItemsService?.patch(id, payload as never, THROW_ON_ERROR);

      await this.reload();
      runInAction(() => {
        this.editingWorkItemId = null;
        this.formData = {};
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.formError =
            err instanceof Error ? err.message : "Failed to update work item";
        }
      });

      console.error("saveWorkItem failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  // ---- Live status ---------------------------------------------------------

  startLiveStatus() {
    if (this.unsubscribeLive) return;
    this.unsubscribeLive = subscribeDevOpsEvents(this.rootStore, (event) =>
      this.handleDevOpsEvent(event)
    );
  }

  /**
   * Apply a live `devops-event` to the work-item list. Only `workItem` events
   * scoped to the open connection/project are handled; the matching row's
   * state/fields are patched in place (no reload). Unknown rows trigger a scoped
   * re-pull so a newly-matching item appears.
   */
  handleDevOpsEvent(event: DevOpsEvent) {
    if (event.entity !== "workItem") return;
    if (
      (event.connectionId && event.connectionId !== this.selectedConnectionId) ||
      (event.projectId && event.projectId !== this.selectedProjectId)
    ) {
      return;
    }
    const workItemId =
      event.workItemId != null ? String(event.workItemId) : null;
    if (!workItemId) return;
    const row = this.workItems.find((w) => w.id === workItemId);
    if (!row) {
      // A new/changed item may now match the query — re-pull (best-effort).
      void this.runQuery();
      return;
    }
    const patch: Partial<DevOpsWorkItem> = {};
    if (event.state) patch.state = event.state;
    const f = event.fields;
    if (f) {
      if (typeof f["System.Title"] === "string") patch.title = f["System.Title"];
      if (typeof f["System.State"] === "string") patch.state = f["System.State"];
      if (typeof f["System.AssignedTo"] === "string")
        patch.assignedTo = f["System.AssignedTo"];
    }
    if (event.at !== undefined) patch.changedDate = event.at ?? null;
    if (Object.keys(patch).length > 0) {
      runInAction(() => {
        this.workItems = this.workItems.map((w) =>
          w.id === workItemId ? { ...w, ...patch } : w
        );
      });
    }
  }

  /** Route onExit — tear down the live subscription. */
  dispose() {
    if (this.unsubscribeLive) {
      this.unsubscribeLive();
      this.unsubscribeLive = null;
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.dispose();
    this.projects = [];
    this.connectionsLoading = false;
    this.projectsLoading = false;
    this.wiql = DEFAULT_WIQL;
    this.workItems = [];
    this.loading = false;
    this.error = null;
    this.searchQuery = "";
    this.stateFilter = "";
    this.typeFilter = "";
    this.sortKey = "id";
    this.sortDir = "desc";
    this.editingWorkItemId = null;
    this.formData = {};
    this.saving = false;
    this.formError = null;
    this.projectsSeq += 1;
    this.workItemsSeq += 1;
    this.unsubscribeLive = null;
  }
}

export default WorkItemsStore;
